/**
 * ITT catalog menu: vertical root list + lazy-loaded flyout panel via Section Rendering API.
 */
class CatalogMenu extends HTMLElement {
  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLElement | null} */
  #activeRootItem = null;

  #documentListenerBound = false;

  connectedCallback() {
    this.#bindEvents();
    this.#preloadFirstRoot();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#removeListeners?.();
    this.#unbindDocumentPointerDown();
  }

  #bindEvents() {
    const onPointerEnter = this.#handleRootPointerEnter.bind(this);
    const onFocusIn = this.#handleRootFocusIn.bind(this);
    const onToggleClick = this.#handleToggleClick.bind(this);
    const onKeyDown = this.#handleKeyDown.bind(this);
    const onPointerLeave = this.#handlePointerLeave.bind(this);

    this.addEventListener('pointerenter', onPointerEnter, true);
    this.addEventListener('focusin', onFocusIn, true);
    this.addEventListener('click', onToggleClick);
    this.addEventListener('keydown', onKeyDown);
    this.addEventListener('pointerleave', onPointerLeave);

    this.#removeListeners = () => {
      this.removeEventListener('pointerenter', onPointerEnter, true);
      this.removeEventListener('focusin', onFocusIn, true);
      this.removeEventListener('click', onToggleClick);
      this.removeEventListener('keydown', onKeyDown);
      this.removeEventListener('pointerleave', onPointerLeave);
    };
  }

  #removeListeners = null;

  #preloadFirstRoot() {
    if (this.closest('[popover]:not(:popover-open)')) return;

    const firstToggle = this.querySelector('[data-catalog-root-toggle]');
    if (!(firstToggle instanceof HTMLButtonElement)) return;

    const preload = () => {
      this.#loadPanel(firstToggle, { activate: false });
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(preload, { timeout: 2000 });
    } else {
      setTimeout(preload, 500);
    }
  }

  /**
   * @param {Event} event
   */
  #handleRootPointerEnter(event) {
    if (!this.#isDesktopPointer()) return;

    const toggle = this.#findToggleFromEvent(event);
    if (!toggle) return;

    this.#activateRoot(toggle);
  }

  /**
   * @param {FocusEvent} event
   */
  #handleRootFocusIn(event) {
    const toggle = this.#findToggleFromEvent(event);
    if (!toggle) return;

    this.#activateRoot(toggle);
  }

  /**
   * @param {Event} event
   */
  #handleToggleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest('[data-catalog-root-toggle]');
    if (!(toggle instanceof HTMLButtonElement)) return;

    event.preventDefault();

    const rootItem = toggle.closest('.catalog-menu__root-item');
    if (!this.#isDesktopPointer() && rootItem instanceof HTMLElement && this.#activeRootItem === rootItem) {
      this.#closePanel();
      return;
    }

    this.#activateRoot(toggle);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.#closePanel();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const toggles = [...this.querySelectorAll('[data-catalog-root-toggle]')].filter(
      (node) => node instanceof HTMLButtonElement
    );

    if (toggles.length === 0) return;

    const activeIndex = toggles.findIndex((toggle) => toggle.closest('.catalog-menu__root-item')?.classList.contains('is-active'));
    const nextIndex =
      event.key === 'ArrowDown'
        ? (activeIndex + 1) % toggles.length
        : (activeIndex <= 0 ? toggles.length - 1 : activeIndex - 1);

    const nextToggle = toggles[nextIndex];
    if (!(nextToggle instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#activateRoot(nextToggle);
    nextToggle.focus();
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerLeave(event) {
    if (!this.#isDesktopPointer()) return;
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#closePanel();
  }

  /**
   * @param {Event} event
   * @returns {HTMLButtonElement | null}
   */
  #findToggleFromEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    const item = target.closest('.catalog-menu__root-item');
    if (!item) return null;

    const toggle = item.querySelector('[data-catalog-root-toggle]');
    return toggle instanceof HTMLButtonElement ? toggle : null;
  }

  /**
   * @param {HTMLButtonElement} toggle
   */
  #activateRoot(toggle) {
    const rootItem = toggle.closest('.catalog-menu__root-item');
    if (!(rootItem instanceof HTMLElement)) return;

    if (this.#activeRootItem && this.#activeRootItem !== rootItem) {
      this.#activeRootItem.classList.remove('is-active');
      const previousToggle = this.#activeRootItem.querySelector('[data-catalog-root-toggle]');
      if (previousToggle instanceof HTMLButtonElement) {
        previousToggle.setAttribute('aria-expanded', 'false');
      }
    }

    this.#activeRootItem = rootItem;
    rootItem.classList.add('is-active');
    toggle.setAttribute('aria-expanded', 'true');

    const panel = this.querySelector('[data-catalog-panel]');
    if (panel instanceof HTMLElement) {
      panel.hidden = false;
    }

    this.#loadPanel(toggle, { activate: true });
    this.#bindDocumentPointerDown();
  }

  #handleDocumentPointerDown = (event) => {
    if (this.#isDesktopPointer()) return;
    if (!this.#activeRootItem) return;

    const target = event.target;
    if (target instanceof Node && this.contains(target)) return;

    this.#closePanel();
  };

  #bindDocumentPointerDown() {
    if (this.#documentListenerBound || this.#isDesktopPointer()) return;
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, true);
    this.#documentListenerBound = true;
  }

  #unbindDocumentPointerDown() {
    if (!this.#documentListenerBound) return;
    document.removeEventListener('pointerdown', this.#handleDocumentPointerDown, true);
    this.#documentListenerBound = false;
  }

  #closePanel() {
    const panel = this.querySelector('[data-catalog-panel]');
    if (panel instanceof HTMLElement) {
      panel.hidden = true;
    }

    if (this.#activeRootItem) {
      this.#activeRootItem.classList.remove('is-active');
      const toggle = this.#activeRootItem.querySelector('[data-catalog-root-toggle]');
      if (toggle instanceof HTMLButtonElement) {
        toggle.setAttribute('aria-expanded', 'false');
      }
      this.#activeRootItem = null;
    }

    this.#unbindDocumentPointerDown();
  }

  /**
   * @param {HTMLButtonElement} toggle
   * @param {{ activate: boolean }} options
   */
  async #loadPanel(toggle, { activate }) {
    const handle = toggle.dataset.handle;
    const panelUrl = toggle.dataset.panelUrl;
    const panelInner = this.querySelector('[data-catalog-panel-inner]');

    if (!handle || !panelUrl || !(panelInner instanceof HTMLElement)) return;

    if (this.#cache.has(handle)) {
      panelInner.innerHTML = this.#cache.get(handle) || '';
      panelInner.classList.remove('is-loading');
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    if (activate) {
      panelInner.classList.add('is-loading');
      panelInner.textContent = this.dataset.loadingLabel || '';
    }

    try {
      const response = await fetch(panelUrl, {
        signal: this.#abortController.signal,
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`Panel fetch failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const panelStyle = doc.querySelector('style[data-section-stylesheet]');
      if (panelStyle instanceof HTMLStyleElement) {
        let injected = document.querySelector('style[data-catalog-menu-panel-styles]');
        if (!(injected instanceof HTMLStyleElement)) {
          injected = document.createElement('style');
          injected.setAttribute('data-catalog-menu-panel-styles', '');
          document.head.appendChild(injected);
        }
        injected.textContent = panelStyle.textContent;
      }
      const panel = doc.querySelector('.catalog-menu-panel');

      const markup = panel instanceof HTMLElement ? panel.outerHTML : '';
      const holder = document.createElement('div');
      holder.innerHTML = markup;
      await this.#hydratePanelChildren(holder, this.#abortController.signal);
      const complete = holder.innerHTML;
      this.#cache.set(handle, complete);

      if (activate || panelInner.classList.contains('is-loading')) {
        panelInner.innerHTML = complete;
        panelInner.classList.remove('is-loading');
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      if (activate) {
        panelInner.classList.remove('is-loading');
        panelInner.textContent = '';
      }
    }
  }

  /**
   * Grandchild links are a separate section render per child, so one flyout
   * does not walk the whole subtree in a single Liquid request.
   * @param {HTMLElement} root
   * @param {AbortSignal} signal
   */
  async #hydratePanelChildren(root, signal) {
    const lists = root.querySelectorAll('[data-catalog-children-url]');

    await Promise.all(
      [...lists].map(async (list) => {
        if (!(list instanceof HTMLElement)) return;

        const url = list.dataset.catalogChildrenUrl;
        if (!url) return;

        delete list.dataset.catalogChildrenUrl;

        try {
          const response = await fetch(url, {
            signal,
            headers: { Accept: 'text/html' },
          });

          if (!response.ok) {
            list.remove();
            return;
          }

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = doc.querySelector('[data-catalog-children-list]');
          const items = source?.innerHTML.trim() || '';

          if (!items) {
            list.remove();
            return;
          }

          list.innerHTML = items;
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') throw error;
          list.remove();
        }
      })
    );
  }

  #isDesktopPointer() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }
}

if (!customElements.get('catalog-menu')) {
  customElements.define('catalog-menu', CatalogMenu);
}
